import { Prejudice } from '@norma/shared/types/prejudice.type';
import { PrintFormat } from '@norma/shared/types/print.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import { Document, Packer } from 'docx';
import { saveAs } from 'file-saver';
import i18next from 'i18next';
import {
  mergeWithBodyTemplate,
  mergeWithCoverTemplate,
} from 'src/containers/client/ClientProcedures/templates';
import { useAppDispatch } from 'src/hooks/store';
import { BookType, WorkBook, writeFile } from 'xlsx';

export const getPrintProcedureFileName = (
  procedure: Procedure,
  victime: Victime,
) =>
  `norma-${
    i18next
      .t(`procedure.fields.procedureType.options.${procedure.procedureType}`)
      ?.toLowerCase() || ''
  }-${victime.referenceDossierVictime.toLowerCase()}`.replaceAll(' ', '');

export const getPrintDossierFileName = (victime: Victime) =>
  `norma-${victime.referenceDossierVictime.toLowerCase()}`.replaceAll(' ', '');

export const getPrintPrejudiceFileName = (
  procedure: Procedure,
  victime: Victime,
  prejudice: Pick<Prejudice, 'type' | 'formType'>,
) =>
  `norma-${
    i18next
      .t(`procedure.fields.procedureType.options.${procedure.procedureType}`)
      ?.toLowerCase() || ''
  }-${victime.referenceDossierVictime.toLowerCase()}-${prejudice.type
    .toLowerCase()
    .replaceAll(' ', '')}`;

const saveAsDocx = (
  documentOrBuffer: Document | Uint8Array,
  fileName: string,
) => {
  if (documentOrBuffer instanceof Uint8Array) {
    const arrayBuffer = new ArrayBuffer(documentOrBuffer.byteLength);
    const uint8 = new Uint8Array(arrayBuffer);
    uint8.set(documentOrBuffer);
    const blob = new Blob([uint8], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    saveAs(blob, `${fileName}.docx`);
  } else {
    Packer.toBlob(documentOrBuffer)
      .then((blob) => saveAs(blob, `${fileName}.docx`))
      .catch((e) => {
        console.log(e);
      });
  }
};

const saveAsXlsx = (
  document: WorkBook,
  fileName: string,
  printFormat: BookType,
) => {
  writeFile(document, `${fileName}.${printFormat}`, { bookType: printFormat });
};

export const saveDocument = async (
  document: Document | WorkBook,
  fileName: string,
  printFormat: PrintFormat,
  organizationId: string | undefined,
  dispatch: ReturnType<typeof useAppDispatch>,
  withoutTemplate: boolean = false,
) => {
  switch (printFormat) {
    case 'docx': {
      if (withoutTemplate) {
        return saveAsDocx(document as Document, fileName);
      } else {
        const mergedDocBuffer = await mergeWithCoverTemplate(
          organizationId,
          document as Document,
          dispatch,
        );
        const mergedDoc = await mergeWithBodyTemplate(
          organizationId,
          mergedDocBuffer,
          dispatch,
        );
        return saveAsDocx(mergedDoc, fileName);
      }
    }
    case 'xlsx':
    case 'csv':
      return saveAsXlsx(document as WorkBook, fileName, printFormat);
  }
};
